import * as React from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { getAppVersions } from '../Api';
import AppVersionDto from '../models/dtos/AppVersionDto';

export const MAX_BUILD_NUMBER = 1_000_000_000;

export const sanitizeBuildNumberInput = (value: string) => value.replace(/[^\d]/g, '');

export const parseBuildNumberInput = (value: string): number | null => {
    const trimmed = sanitizeBuildNumberInput(value).trim();
    if (trimmed.length === 0) {
        return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
};

export function findBuildNumberOption(
    versions: AppVersionDto[],
    buildNumber: number | null | undefined,
): AppVersionDto | null {
    if (buildNumber == null) {
        return null;
    }
    return versions.find(option => option.buildNumber === buildNumber) ?? null;
}

export function formatBuildNumberOptionLabel(option: AppVersionDto): string {
    return `${option.buildVersion} (${option.buildNumber})`;
}

export function useAppVersionsForOsTypes(osTypes: string[]) {
    const [versions, setVersions] = React.useState<AppVersionDto[]>([]);
    const [loading, setLoading] = React.useState(false);
    const osTypesKey = osTypes.slice().sort().join(',');

    React.useEffect(() => {
        let cancelled = false;
        const typesToLoad = osTypes.length > 0 ? osTypes : ['android', 'ios'];

        (async () => {
            setLoading(true);
            try {
                const lists = await Promise.all(typesToLoad.map(osType => getAppVersions(osType)));
                if (cancelled) {
                    return;
                }
                const merged = lists
                    .flat()
                    .filter((item): item is AppVersionDto => item != null)
                    .sort((left, right) => right.buildNumber - left.buildNumber);
                const seen = new Set<string>();
                const unique = merged.filter(item => {
                    const key = `${item.osType}:${item.buildNumber}`;
                    if (seen.has(key)) {
                        return false;
                    }
                    seen.add(key);
                    return true;
                });
                setVersions(unique);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [osTypesKey]);

    return { versions, loading };
}

export function distinctAppVersionNames(versions: AppVersionDto[]): string[] {
    const seen = new Set<string>();
    const names: string[] = [];
    versions.forEach(version => {
        const name = version.buildVersion.trim();
        if (name.length === 0 || seen.has(name)) {
            return;
        }
        seen.add(name);
        names.push(name);
    });
    return names;
}

type BuildNumberAutocompleteProps = {
    label: string;
    value: number | null;
    onChange: (value: number | null) => void;
    versions: AppVersionDto[];
    loading?: boolean;
    peerMin?: number | null;
    peerMax?: number | null;
    helperText?: string;
    fullWidth?: boolean;
};

export function BuildNumberAutocomplete({
    label,
    value,
    onChange,
    versions,
    loading = false,
    peerMin,
    peerMax,
    helperText,
    fullWidth = true,
}: BuildNumberAutocompleteProps) {
    const [inputValue, setInputValue] = React.useState(value == null ? '' : String(value));

    React.useEffect(() => {
        setInputValue(value == null ? '' : String(value));
    }, [value]);

    const filteredOptions = versions.filter(option => {
        if (peerMax != null && option.buildNumber > peerMax) {
            return false;
        }
        if (peerMin != null && option.buildNumber < peerMin) {
            return false;
        }
        return true;
    });

    const applyInput = (nextValue: string) => {
        const sanitized = sanitizeBuildNumberInput(nextValue);
        setInputValue(sanitized);
        onChange(parseBuildNumberInput(sanitized));
    };

    return (
        <Autocomplete
            freeSolo
            clearOnEscape
            fullWidth={fullWidth}
            options={filteredOptions}
            loading={loading}
            value={findBuildNumberOption(versions, value)}
            inputValue={inputValue}
            onChange={(_, option) => {
                if (typeof option === 'string') {
                    applyInput(option);
                    return;
                }
                applyInput(option != null ? String(option.buildNumber) : '');
            }}
            onInputChange={(_, nextValue, reason) => {
                if (reason !== 'reset') {
                    applyInput(nextValue);
                }
            }}
            getOptionLabel={option => (typeof option === 'string' ? option : String(option.buildNumber))}
            isOptionEqualToValue={(left, right) => left.buildNumber === right.buildNumber}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    {formatBuildNumberOptionLabel(option)}
                </Box>
            )}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    helperText={helperText ?? 'Pick a registered build or type a build number.'}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
}

type AppVersionNameAutocompleteProps = {
    label: string;
    value: string | null;
    onChange: (value: string | null) => void;
    versions: AppVersionDto[];
    loading?: boolean;
    helperText?: string;
    fullWidth?: boolean;
};

export function AppVersionNameAutocomplete({
    label,
    value,
    onChange,
    versions,
    loading = false,
    helperText,
    fullWidth = true,
}: AppVersionNameAutocompleteProps) {
    const options = React.useMemo(() => distinctAppVersionNames(versions), [versions]);

    return (
        <Autocomplete
            freeSolo
            clearOnEscape
            fullWidth={fullWidth}
            options={options}
            loading={loading}
            value={value ?? ''}
            onChange={(_, option) => {
                const next = typeof option === 'string' ? option.trim() : '';
                onChange(next.length > 0 ? next : null);
            }}
            onInputChange={(_, option) => {
                const trimmed = option.trim();
                onChange(trimmed.length > 0 ? trimmed : null);
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    helperText={helperText ?? 'Pick a registered app version name or type one.'}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
}
